import type { Page } from '../../types';
import { COUNTRIES } from '../../defaultContent';

const addDetectedFraud: Page[] = [
  {
    pageType: 'information',
    title: 'Before you start',
    name: 'detected-fraud-before-you-start',
    description:
      'This typology refers to the amount of fraud detected within the given quarter. Detected fraud includes instances of identified fraud, where, post-payment, it was decided that on the balance of probabilities the intention was to defraud. For reporting purposes, this should not include instances of fraud that were prevented from payment after the point of detection (these should be reported in Section 2). Entries should record the number of instances and the value of the loss relating to the fraud. Entries must include selections from the following typology dropdowns:<ul><li>the business area affected by the fraud</li><li>who committed the fraud?</li><li>the nature of deception - whether the prevented fraud involved a false representation, a failure to declare or disclose, or an abuse of position</li><li>the detailed nature of deception - a dependent dropdown selection on the specifics of the fraudulent activity</li><li>if applicable, the fraud enabler(s)</li><li>whether or not the fraud related to a COVID-19 scheme or activity</li></ul><br/>',
  },
  {
    pageType: 'question',
    title: 'Detected fraud',
    name: 'detected-fraud',
    description:
      'This section refers to all instances of fraud that were detected (after the event) in the given quarter. Instances should be reported in the CDR in the quarter it was determined to be fraud. For example, where a fraud occurred in quarter 2 but was undetected until quarter 4, this would be included in the Q4 return. All instances of detected fraud should refer to actual instances and values, not estimates.\n' +
      'Detected Fraud should be reported at the point at which it has been determined fraud, on the civil balance of probabilities, regardless of whether prosecution to the criminal standard is being progressed. This can include instances where fraud is proven to both the civil and criminal standards simultaneously.\n' +
      'Please note: All entries in this section must be attributed to a selection from the typology dropdowns. Please refer to the Typologies Definition document for more detail<br><br>',
    questions: [
      {
        questionType: 'radio',
        title: 'Do you have any instances of detected fraud to declare?',
        name: 'detected_fraud_declare',
        options: ['Yes', 'No'],
        rules: {
          required: true,
        },
      },
    ],
  },
  {
    pageType: 'list',
    title: 'Detected fraud',
    name: 'detected-fraud-add-instance',
    type: 'nilreturn',
    fastToggle: true,
    total: {
      columns: [
        { title: 'Number of instances', field: 'detected_fraud_input' },
        { title: 'Total Value', field: 'detected_fraud' },
      ],
    },
    description:
      'Detected Fraud should be reported at the point at which it has been determined fraud, on the civil balance of probabilities, regardless of whether prosecution to the criminal standard is being progressed. This can include instances where fraud is proven to both the civil and criminal standards simultaneously.<br/><br/>Please note: All entries in this section must be attributed to a selection from the typology dropdowns. Please refer to the Typologies Definition document for more detail<br><br>',
    disabled: [
      {
        field: 'detected_fraud_declare',
        condition: 'equals', // optional
        value: 'No',
      },
    ],
    questions: [
      {
        questionType: 'select',
        title: 'Which spending area did the fraud occur in?',
        name: 'detected_fraud_area',
        detailsHeading: 'Help with spending areas',
        detailsContent:
          ' <b>Benefits:</b> money given by the government to support members of the public' +
          ' <br/><b>Charges & other non-tax income:</b> money owed to public sector organisations (for example, court fines or prescription fees)' +
          ' <br/><b>Grants:</b> money given by a public sector organisation for a specific purpose' +
          ' <br/><b>Licences and permits:</b> licences, permits or certificates that grant permissions (for example, a Blue Badge, Surveyor licence, marriage certificate)' +
          ' <br/><b>Loans & credit guarantees:</b> money loaned or guaranteed by a public sector organisation' +
          ' <br/><b>Personnel:</b> employees (including contractors and agency workers)' +
          ' <br/><b>Procurement:</b> goods or services bought by public sector organisations (including suppliers)' +
          ' <br/><b>Special payments:</b> money given in unique or specific circumstances (for example, compensation)' +
          ' <br/><b>Subsidies:</b> support or incentives given by the government' +
          ' <br/><b>Tax & duties:</b> mandatory government charge for goods, income or services',
        options: [
          'Benefits',
          'Charges & other non-tax income',
          'Grants',
          'Licences and permits',
          'Loans & credit guarantees',
          'Personnel',
          'Procurement',
          'Special payments',
          'Subsidies',
          'Tax & duties',
        ],
        hintOption: 'Please Select',
        rules: {
          required: true,
        },
      },
      {
        name: 'detected_fraud_deception',
        questionType: 'radio',
        title: 'What was the general nature of the deception?',
        detailsHeading: 'Help with general nature of deception',
        detailsContent:
          ' <b>Abuse of position:</b> use of a person&apos;s position or role in the government or public sector' +
          ' <br/><b>Failure to declare/disclose:</b> Intentionally omitting information you legally need to share' +
          ' <br/><b>False representation:</b> intentionally hiding or giving misleading information' +
          ' <br/><b>Unknown:</b> I don&apos;t know how the deception was committed',
        options: [
          {
            option: 'Abuse of position',
            questions: [
              {
                questionType: 'select',
                title: 'Specific nature of deception',
                detailsHeading: 'Help with specific nature of deception',
                detailsContent:
                  '<b>Allowances:</b> abusing authority to make allowance payments' +
                  '<br/><b>Application for employment:</b> abusing authority to recruit an unqualified candidate for personal benefit' +
                  '<br/><b>Asset misappropriation:</b> misuse of government property (for example, a laptop or phone)' +
                  '<br/><b>Contract rigging:</b> bidding for or being awarded a contract using false information' +
                  '<br/><b>Falsified application:</b> using internal access when applying for HMG Services (for example, a passport or student loan)' +
                  '<br/><b>Insider theft of assets:</b> theft of government property (for example, a laptop or phone)' +
                  '<br/><b>Mandate fraud:</b> changing a payment mandate (for example, details for a standing order)' +
                  '<br/><b>Misuse of purchasing card:</b> using corporate card details to buy unauthorised goods or services e.g GPC card, fuel card' +
                  '<br/><b>Payment fraud:</b> creating or diverting payments (for example, false or duplicate invoices)',
                name: 'detected_fraud_positions',
                hintOption: 'Please Select',
                options: [
                  'Allowances',
                  'Application for employment',
                  'Asset misappropriation',
                  'Contract rigging',
                  'Falsified application',
                  'Insider theft of assets',
                  'Mandate fraud',
                  'Misuse of purchasing card',
                  'Payment fraud',
                ],
                rules: {
                  required: true,
                },
              },
            ],
          },
          {
            option: 'Failure to declare/disclose',
            questions: [
              {
                questionType: 'select',
                title: 'Specific nature of deception',
                detailsHeading: 'Help with specific nature of deception',
                detailsContent:
                  '<b>Abuse of working hours:</b> omitting relevant information that would affect eligibility to continue claiming flexi-time or annual leave' +
                  '<br/><b>Allowances:</b> omitting relevant information in continue claiming for government allowances' +
                  '<br/><b>Application for employment:</b> omitting relevant information to continue working in a role (for example, no longer holding a professional certification)' +
                  '<br/><b>Asset misappropriation:</b> not disclosing information that facilitates misuse of government property (for example, moving abroad and continuing to use a laptop or phone)' +
                  '<br/><b>Change of circumstances:</b> not disclosing a change of circumstances that affects eligibility for a service, contract or application' +
                  '<br/><b>Charge evasion:</b> omitting relevant information to evade paying money (for example, fees, taxes or fines)' +
                  '<br/><b>Expense claims:</b> omitting relevant information in continue claiming for government expenses (for example, travel or food)' +
                  '<br/><b>Misuse of funding:</b> not disclosing using funds outside the conditions of the agreement',
                name: 'detected_fraud_disclose',
                hintOption: 'Please Select',
                options: [
                  'Abuse of working hours',
                  'Allowances',
                  'Application for employment',
                  'Asset misappropriation',
                  'Change of circumstances',
                  'Charge evasion',
                  'Expense claims',
                  'Misuse of funding',
                ],
                rules: {
                  required: true,
                },
              },
            ],
          },
          {
            option: 'False representation',
            questions: [
              {
                questionType: 'select',
                title: 'Specific nature of deception',
                detailsHeading: 'Help with specific nature of deception',
                detailsContent:
                  '<b>Abuse of working hours:</b> falsely claiming time worked (for example, flexi-time or annual leave)' +
                  '<br/><b>Allowances:</b> falsely claiming on government allowances' +
                  '<br/><b>Application for employment:</b> using misleading information to get a role (for example, employment history or qualifications)' +
                  '<br/><b>Asset misappropriation:</b> misuse of government property (for example, a laptop or phone)' +
                  '<br/><b>Charge evasion:</b> giving misleading information to evade paying money (for example, fees, taxes or fines)' +
                  '<br/><b>Expense claims:</b> falsely claiming on government expenses (for example, travel or food)' +
                  '<br/><b>Falsified application:</b> using misleading information when applying for HMG Services (for example, a passport or student loan)' +
                  '<br/><b>Falsified invoice:</b> giving misleading information on an invoice (for example, inflating costs)' +
                  '<br/><b>Impersonation of purchasing cardholder:</b> use of corporate card details through impersonation of a public sector employee or official' +
                  '<br/><b>Mandate fraud:</b> changing a payment mandate (for example, details for a standing order)' +
                  '<br/><b>Misuse of funding:</b> using funds outside the conditions of the agreement' +
                  '<br/><b>Product substitution or misselling:</b> using or providing a different product or service than promised or misrepresenting the need for a product or service',
                name: 'detected_fraud_representation',
                hintOption: 'Please Select',
                options: [
                  'Abuse of working hours',
                  'Allowances',
                  'Application for employment',
                  'Asset misappropriation',
                  'Charge evasion',
                  'Expense claims',
                  'Falsified application',
                  'Falsified invoice',
                  'Impersonation of purchasing card holder',
                  'Mandate fraud',
                  'Misuse of funding',
                  'Product substitution or misselling',
                ],
                rules: {
                  required: true,
                },
              },
            ],
          },
          {
            option: 'Unknown',
            questions: [
              {
                questionType: 'select',
                title: 'Specific nature of deception',
                detailsHeading: 'Help with specific nature of deception',
                detailsContent: "I don't know how the deception was committed",
                name: 'detected_fraud_unknown',
                hintOption: 'Please Select',
                options: ['Unknown'],
                rules: {
                  required: true,
                },
              },
            ],
          },
        ],
        rules: {
          required: false,
        },
      },
      {
        questionType: 'radio',
        title: 'Who committed the fraud?',
        name: 'detected_fraud_committed',
        hint: 'Select one option',
        detailsHeading: 'Who was responsible for the fraud?',
        detailsContent:
          '<b>Contingent labour:</b> in their role as a person contracted to supply a service or contingent labour to government' +
          '<br/><b>Hostile third party:</b> (for example, foreign states, "hacktivist" groups and organised crime groups)' +
          '<br/><b>Members of the general public:</b> in their role as a member of the public' +
          '<br/><b>Other public servants:</b> in their role as a person supporting the government without pay (for example, jurors, volunteers or magistrates)' +
          '<br/><b>Private sector - company:</b> a business owned by shareholders but run by its directors' +
          '<br/><b>Private sector - partnerships:</b> a business owned and run by its partners' +
          '<br/><b>Private sector - self employed:</b> in their role as a self-employed person (for example, a sole trader)' +
          '<br/><b>Public sector employees:</b> in their role as a person employed by the government (for example, a public servant or health care employee)' +
          '<br/><b>Public sector organisations:</b> (for example, NHS Trusts, higher education providers and academies)' +
          '<br/><b>Third sector organisations:</b> a non-governmental organisation (for example, charities, social enterprises, and voluntary groups)' +
          '<br/><b>Unknown:</b> I don&apos;t know who was responsible for the fraud',
        options: [
          'Contingent labour',
          'Hostile third party',
          'Members of the general public',
          'Other public servants',
          'Private sector - company',
          'Private sector - partnerships',
          'Private sector - self employed',
          'Public sector employees',
          'Public sector organisations',
          'Third sector organisations',
          'Unknown',
        ],
        rules: { required: false },
      },
      {
        questionType: 'instanceInput',
        title: 'How many instances occurred?',
        name: 'detected_fraud_input',
        hint: `Total number of instances`,
        rules: {
          required: true,
        },
      },
      {
        questionType: 'costInput',
        displaySeperator: 'thousand',
        title: 'What was the value of the fraud?',
        name: 'detected_fraud',
        hint: `Total value`,
        rules: {
          required: true,
          decimal: 'decimal(2)',
        },
      },
      {
        questionType: 'textarea',
        title: 'Comments',
        hideOptionalLabel: true,
        name: 'detected_fraud_comments',
        hint: `Please ensure you do not include any personal or sensitive information`,
        rules: {
          required: false,
        },
      },
      {
        questionType: 'checkbox',
        title: 'What supported the fraud (fraud enablers)?',
        hint: 'Select all that apply',
        detailsHeading: 'What were the fraud enablers?',
        detailsContent:
          '<b>AI (artificial intelligence):</b> using artificial intelligence to create or present false information' +
          '<br><b>Bribery:</b> something given or promised to influence behaviour' +
          '<br><b>Coercion:</b> forced agreement or cooperation (for example, due to physical threats)' +
          '<br><b>Collusion:</b> deceptive agreement or cooperation (for example, due to lies or manipulation)' +
          '<br><b>Conflict of interest:</b> not sharing relationships which resulted in personal gain' +
          '<br><b>False identity:</b> using false documents as person or company (for example, a passport or birth certificate)' +
          '<br><b>Phishing, vishing and SMShing:</b> use cyber vulnerabilities using digital communication methods (for example, clicking a malicious link)' +
          '<br><b>Social media:</b> using this to present disinformation (for example, falsifying personal details)',
        name: 'detected_fraud_support',
        options: [
          'AI (artificial intelligence)',
          'Bribery',
          'Coercion',
          'Collusion',
          'Conflict of interest',
          'False identity',
          'Phishing, vishing and SMShing',
          'Social media',
        ],
        rules: { required: false },
      },
      {
        questionType: 'radio',
        title: 'Does this relate to BAU or C19 activity?',
        name: 'detected_fraud_c19',
        options: ['Business as usual (BAU)', 'Covid-19 (C19)'],
        rules: { required: true },
      },
    ],
  },
];
export default addDetectedFraud;
